import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Video, Heart, Play, Sparkles, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { compressVideoClientSide } from '../utils/compressVideo';

const STATUS_BADGES = {
  processing: { label: 'Processing', className: 'bg-blue-100 text-blue-700', icon: Loader2 },
  pending: { label: 'Pending Review', className: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Live', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Not Approved', className: 'bg-red-100 text-red-700', icon: XCircle },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function VideosTab({ services }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => { fetchVideos(); }, []);

  // Poll while any video is in the `processing` state. Stops automatically
  // once every row has settled (pending/approved/rejected/failed). Gives the
  // Processing badge a clean "flips to Pending Review on its own" feel
  // instead of forcing a manual page refresh.
  useEffect(() => {
    const stillProcessing = videos.some(v => v.status === 'processing');
    if (!stillProcessing) return undefined;
    const interval = setInterval(fetchVideos, 4000);
    return () => clearInterval(interval);
  }, [videos]);

  const fetchVideos = async () => {
    try {
      const res = await api.get('/providers/me/videos');
      setVideos(res.data || []);
    } catch (err) { console.error('Failed to load videos:', err); }
    finally { setLoading(false); }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowed = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mpeg'];
    if (!allowed.includes(file.type)) {
      toast.error('Only MP4, WebM, or MOV files');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setVideoFile(null); setVideoPreview(null);
    setCaption(''); setServiceId(''); setShowForm(false);
    setUploadProgress(0);
    setCompressing(false);
    setCompressProgress(0);
  };

  const handleUpload = async () => {
    if (!videoFile) { toast.error('Please select a video'); return; }
    setUploading(true);
    setUploadProgress(0);
    // Client-side pre-compression for big files, roughly halves upload
    // time on 4G for a typical 1080p phone clip. Server transcodes again
    // to enforce the final 720p H.264 spec regardless.
    let fileToUpload = videoFile;
    if (videoFile.size > 8 * 1024 * 1024) {
      setCompressing(true);
      setCompressProgress(0);
      try {
        const compressed = await compressVideoClientSide(videoFile, (p) => {
          setCompressProgress(Math.round(p * 100));
        });
        if (compressed && compressed !== videoFile && compressed.size < videoFile.size) {
          fileToUpload = compressed;
        }
      } catch (err) {
        console.warn('Client-side compression skipped:', err);
      } finally {
        setCompressing(false);
      }
    }

    const formData = new FormData();
    formData.append('video', fileToUpload);
    formData.append('caption', caption);
    formData.append('service_id', serviceId === 'none' ? '' : serviceId);
    try {
      const res = await api.post('/providers/me/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min safety net; the server now returns fast via BackgroundTasks
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      setVideos(prev => [res.data, ...prev]);
      resetForm();
      toast.success('Uploaded. We are optimising your clip for the feed, this takes under a minute.');
    } catch (err) {
      // If the proxy cut the connection *after* upload finished, the
      // server already has the bytes and has kicked off processing.
      // Refetch the list so the provider sees the Processing row
      // instead of a scary "upload failed" message.
      const wasTimeout = err.code === 'ECONNABORTED' || !err.response;
      if (wasTimeout) {
        try {
          const res = await api.get('/providers/me/videos');
          const latest = res.data || [];
          setVideos(latest);
          const justProcessing = latest.find(v => v.status === 'processing');
          if (justProcessing) {
            resetForm();
            toast.success('Uploaded. We are optimising your clip for the feed, this takes under a minute.');
            return;
          }
        } catch {
          // fall through to the generic error toast below
        }
      }
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/providers/me/videos/${id}`);
      setVideos(prev => prev.filter(v => v.video_id !== id));
      toast.success('Video deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="videos-tab">
      {/* Quality Tips Banner */}
      <div className="bg-gradient-to-r from-[#F9F6F0] to-[#FFF8E7] border border-[#E6E4DD] rounded-xl p-5" data-testid="video-quality-tips">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div>
            <h4 className="font-medium text-[#1A1A1A] text-sm mb-1.5">Tips for great feed videos</h4>
            <ul className="text-xs text-stone-500 space-y-1">
              <li>Film in good lighting, natural light works best</li>
              <li>Keep videos short and engaging (15-60 seconds)</li>
              <li>Show your work process or the final result</li>
              <li>Use portrait orientation (9:16) for the best feed experience</li>
              <li>Any resolution is fine, we optimise every upload to 720p for fast playback on every phone</li>
            </ul>
            <p className="text-xs text-[#D4AF37] mt-2 font-medium">All uploads are reviewed by our team before appearing in the feed.</p>
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
              <Video className="h-5 w-5 text-[#D4AF37]" />
              Video Feed Content
            </h3>
            <p className="text-sm text-stone-400 mt-0.5">Upload short clips of your work. They appear in the client video feed.</p>
          </div>
          <Button variant="outline" onClick={() => setShowForm(!showForm)} className="text-sm" data-testid="add-video-btn">
            <Plus className="h-4 w-4 mr-1" />
            {showForm ? 'Cancel' : 'Upload Video'}
          </Button>
        </div>

        {/* Upload Form */}
        {showForm && (
          <div className="mb-6 p-5 bg-[#F9F6F0] rounded-lg space-y-4" data-testid="video-upload-form">
            <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={e => handleFileSelect(e.target.files?.[0])} />
            <button
              onClick={() => fileRef.current?.click()}
              className={`w-full aspect-video rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors overflow-hidden ${videoPreview ? 'border-[#D4AF37] bg-black' : 'border-stone-300 hover:border-[#D4AF37] bg-stone-50'}`}
              data-testid="video-file-upload"
            >
              {videoPreview ? (
                <video src={videoPreview} className="w-full h-full object-contain" muted controls />
              ) : (
                <>
                  <Upload className="h-8 w-8 text-stone-400 mb-2" />
                  <span className="text-sm text-stone-400">Click to select a video</span>
                  <span className="text-xs text-stone-300 mt-1">MP4, WebM, or MOV, max 50MB</span>
                </>
              )}
            </button>

            <div>
              <Label className="text-xs">Caption (optional, max 200 chars)</Label>
              <Input
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="e.g. Knotless braids transformation in 3 hours"
                className="input-zuriya"
                maxLength={200}
                data-testid="video-caption"
              />
            </div>

            <div>
              <Label className="text-xs">Link to Service (recommended for bookings)</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger className="input-zuriya" data-testid="video-service-select">
                  <SelectValue placeholder="Choose a service..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(services || []).map(s => (
                    <SelectItem key={s.service_id} value={s.service_id}>{s.name}, £{s.price}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(compressing || uploading) && (
              <div className="space-y-1.5" data-testid="video-upload-progress">
                <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-[#D4AF37] rounded-full transition-all duration-300"
                    style={{ width: `${compressing ? compressProgress : uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-stone-500 text-center">
                  {compressing
                    ? `Compressing on device... ${compressProgress}%`
                    : `Uploading... ${uploadProgress}%`}
                </p>
              </div>
            )}

            <Button onClick={handleUpload} disabled={uploading || compressing || !videoFile} className="btn-primary w-full" data-testid="submit-video-btn">
              {compressing
                ? `Compressing... ${compressProgress}%`
                : uploading
                  ? `Uploading... ${uploadProgress}%`
                  : 'Upload Video'}
            </Button>
          </div>
        )}

        {/* Video List */}
        {videos.length === 0 ? (
          <div className="text-center py-10" data-testid="videos-empty">
            <Play className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 mb-1">No videos yet</p>
            <p className="text-xs text-stone-400">Upload short clips to appear in the client video feed and drive bookings.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="videos-list">
            {videos.map(v => {
              const badge = STATUS_BADGES[v.status] || STATUS_BADGES.pending;
              const BadgeIcon = badge.icon;
              return (
                <div key={v.video_id} className="group relative rounded-lg overflow-hidden bg-black aspect-[9/16]" data-testid={`video-item-${v.video_id}`}>
                  {v.poster_url ? (
                    <img src={v.poster_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : v.video_url ? (
                    <video src={v.video_url} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  )}
                  {/* Status badge */}
                  <div className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`} data-testid={`video-status-${v.video_id}`}>
                    <BadgeIcon className={`h-3 w-3 ${v.status === 'processing' ? 'animate-spin' : ''}`} />
                    {badge.label}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                    {v.caption && <p className="text-white text-xs line-clamp-2 mb-1">{v.caption}</p>}
                    {v.service_name && <p className="text-[#D4AF37] text-xs font-medium">{v.service_name}</p>}
                    <div className="flex items-center gap-2 mt-1.5 text-white/60 text-xs">
                      <Heart className="h-3 w-3" /> {v.likes || 0}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(v.video_id)}
                    className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`delete-video-${v.video_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-white" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
